<template>
  <div class="task-detail-page" v-loading="loading">
    <WelcomeBanner title="任务详情">
      <div slot="right">
        <el-button type="text" icon="el-icon-arrow-left" size="small" class="back-button" @click="goBack">返回</el-button>
      </div>
    </WelcomeBanner>

    <BaseCard>
      <el-row :gutter="20" class="detail-content">
      <el-col :span="16">
        <!-- 任务基本信息 -->
        <el-card class="info-card">
          <div slot="header" class="card-header">
            <span class="header-title">任务详情</span>
            <el-tag :type="getStatusTagType(task.status)" size="small">{{ getStatusText(task.status) }}</el-tag>
          </div>
          <div class="basic-info">
            <div class="task-priority-badge">
              <el-tag :type="getPriorityTagType(task.priority)" effect="plain" size="medium">{{ getPriorityText(task.priority) }}优先级</el-tag>
            </div>
            <h2 class="task-title">{{ task.task_name }}</h2>
            <p class="desc">{{ task.description || '暂无描述' }}</p>
            <div class="meta">
              <span><i class="el-icon-folder"></i> 项目：{{ task.project_id }}</span>
              <span><i class="el-icon-s-custom"></i> 执行人：{{ task.receiver_id }}</span>
              <span><i class="el-icon-user"></i> 创建人：{{ task.sender_id }}</span>
            </div>
          </div>
        </el-card>

        <!-- 任务进度 -->
        <el-card class="task-progress" style="margin-top: 20px;">
          <div slot="header" class="card-header">
            <span class="header-title">任务进度</span>
            <span class="progress-count">{{ task.completed_sub_tasks || 0 }}/{{ task.total_sub_tasks || 0 }}</span>
          </div>
          <div class="progress-content">
            <div class="progress-summary">
              <span class="progress-label">完成进度</span>
              <span class="progress-value">{{ task.progress || 0 }}%</span>
            </div>
            <el-progress :percentage="task.progress || 0" :stroke-width="16" :show-text="false"></el-progress>
          </div>
        </el-card>

        <!-- 子任务列表 -->
        <el-card class="sub-tasks" style="margin-top: 20px;">
          <div slot="header" class="card-header">
            <span class="header-title">子任务列表</span>
            <span class="task-count" v-if="subTasks.length">{{ subTasks.length }} 个子任务</span>
          </div>
          <div v-if="subTasks.length">
            <el-table :data="subTasks" style="width: 100%" stripe>
              <el-table-column prop="task_name" label="子任务名称" min-width="200" show-overflow-tooltip />
              <el-table-column prop="status" label="状态" width="100" align="center">
                <template slot-scope="scope">
                  <el-tag :type="getStatusTagType(scope.row.status)" size="small">{{ getStatusText(scope.row.status) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="receiver_id" label="执行人" width="120" show-overflow-tooltip />
              <el-table-column prop="finish_time" label="截止时间" width="140">
                <template slot-scope="scope">{{ formatTime(scope.row.finish_time) }}</template>
              </el-table-column>
              <el-table-column label="操作" width="90" align="center">
                <template slot-scope="scope">
                  <el-button type="text" size="small" @click="goToDetail(scope.row.task_id)">查看</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
          <div v-else class="empty-state">
            <i class="el-icon-document" style="font-size: 48px; color: #dcdfe6;"></i>
            <p>暂无子任务</p>
          </div>
        </el-card>

        <!-- 任务关系树 -->
        <el-card class="task-tree" style="margin-top: 20px;">
          <div slot="header" class="card-header">
            <span class="header-title">任务关系树</span>
          </div>
          <TaskTree :taskId="task.root_task_id || taskId" />
        </el-card>

        <!-- 任务依赖 -->
        <el-card class="task-dependencies" style="margin-top: 20px;">
          <div slot="header" class="card-header">
            <span class="header-title">任务依赖关系</span>
            <span class="task-count" v-if="dependencies.length">{{ dependencies.length }} 个依赖</span>
          </div>
          <div v-if="dependencies.length">
            <div class="dependency-list">
              <div v-for="dep in dependencies" :key="dep.task_id" class="dependency-item">
                <div class="dep-info">
                  <div class="dep-name">{{ dep.task_name }}</div>
                  <div class="dep-status">
                    <el-tag :type="getStatusTagType(dep.status)" size="small">{{ getStatusText(dep.status) }}</el-tag>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="el-icon-connection" style="font-size: 48px; color: #dcdfe6;"></i>
            <p>无任务依赖</p>
          </div>
        </el-card>
      </el-col>

      <el-col :span="8">
        <!-- 任务信息 -->
        <el-card class="info-sidebar">
          <div slot="header" class="card-header">
            <span class="header-title">任务信息</span>
          </div>
          <div class="info-list">
            <div class="info-item">
              <span class="label">任务ID</span>
              <span class="value code">{{ task.task_id }}</span>
            </div>
            <div class="info-item">
              <span class="label">创建时间</span>
              <span class="value">{{ formatTime(task.create_time) }}</span>
            </div>
            <div class="info-item">
              <span class="label">截止时间</span>
              <span class="value">{{ formatTime(task.finish_time) || '未设置' }}</span>
            </div>
            <div class="info-item">
              <span class="label">负责人</span>
              <span class="value">{{ task.receiver_id }}</span>
            </div>
            <div class="info-item">
              <span class="label">创建人</span>
              <span class="value">{{ task.sender_id }}</span>
            </div>
            <div class="info-item">
              <span class="label">优先级</span>
              <span class="value">
                <el-tag :type="getPriorityTagType(task.priority)" size="mini">{{ getPriorityText(task.priority) }}</el-tag>
              </span>
            </div>
            <div class="info-item">
              <span class="label">所属项目</span>
              <span class="value">{{ task.project_id }}</span>
            </div>
            <div class="info-item">
              <span class="label">状态</span>
              <span class="value">
                <el-tag :type="getStatusTagType(task.status)" size="mini">{{ getStatusText(task.status) }}</el-tag>
              </span>
            </div>
          </div>
        </el-card>

        <!-- 任务历史 -->
        <el-card class="task-history" style="margin-top: 20px;">
          <div slot="header" class="card-header">
            <span class="header-title">任务历史</span>
            <span class="task-count" v-if="taskHistory.length">{{ taskHistory.length }} 条记录</span>
          </div>
          <div v-if="taskHistory.length">
            <div class="history-list">
              <div v-for="item in taskHistory" :key="item.history_id || item.id" class="history-item">
                <div class="history-time">{{ formatTime(item.create_time) }}</div>
                <div class="history-content">{{ item.content }}</div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="el-icon-time" style="font-size: 48px; color: #dcdfe6;"></i>
            <p>无任务历史记录</p>
          </div>
        </el-card>

        <!-- 关联会话 -->
        <el-card class="session-association" style="margin-top: 20px;">
          <div slot="header" class="card-header">
            <span class="header-title">关联会话</span>
            <span class="task-count" v-if="relatedSessions.length">{{ relatedSessions.length }} 个会话</span>
          </div>
          <div v-if="relatedSessions.length">
            <div class="session-list">
              <div v-for="session in relatedSessions" :key="session.session_id" class="session-item">
                <div class="session-name">{{ session.content }}</div>
                <div class="session-time">{{ formatTime(session.create_time) }}</div>
                <el-button type="text" size="small" @click="goToSessionDetail(session.session_id)">查看会话</el-button>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="el-icon-chat-dot-round" style="font-size: 48px; color: #dcdfe6;"></i>
            <p>无关联会话</p>
          </div>
        </el-card>

        <!-- 操作按钮 -->
        <el-card class="action-card" style="margin-top: 20px;">
          <div class="action-buttons">
            <el-button type="primary" icon="el-icon-edit" style="width: 100%; margin-bottom: 12px;" @click="editTask">编辑任务</el-button>
            <el-button type="danger" icon="el-icon-delete" plain style="width: 100%;" @click="deleteTask">删除任务</el-button>
          </div>
        </el-card>
      </el-col>
    </el-row>
    </BaseCard>
  </div>
</template>

<script>
import { getTaskDetail, getSubTasks, getTaskDependencies, getTaskHistory, getRelatedSessions, deleteTask } from '../api/task'
import BaseCard from '@/components/BaseCard'
import WelcomeBanner from '@/components/WelcomeBanner'
import TaskTree from '@/components/TaskTree'

export default {
  name: 'TaskDetail',
  components: { BaseCard, WelcomeBanner, TaskTree },
  data() {
    return {
      taskId: null,
      task: {},
      subTasks: [],
      dependencies: [],
      taskHistory: [],
      relatedSessions: [],
      loading: false
    }
  },
  created() {
    this.taskId = this.$route.params.id
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        // 加载任务详情 - 拦截器已返回 res.data
        this.task = await getTaskDetail(this.taskId) || {}

        // 加载子任务
        this.subTasks = await getSubTasks(this.taskId) || []

        // 加载任务依赖
        this.dependencies = await getTaskDependencies(this.taskId) || []

        // 加载任务历史
        this.taskHistory = await getTaskHistory(this.taskId) || []

        // 加载关联会话
        this.relatedSessions = await getRelatedSessions(this.taskId) || []
      } catch (error) {
        console.error('加载任务详情失败:', error)
        this.$message.error(`加载失败: ${error.message || '未知错误'}`)
      } finally {
        this.loading = false
      }
    },
    getStatusText(status) {
      const statusMap = {
        '1': '待执行',
        '2': '等依赖',
        '3': '执行中',
        '4': '已完成',
        '5': '已失败'
      }
      return statusMap[status] || status
    },
    getStatusTagType(status) {
      const typeMap = {
        '1': 'warning',
        '2': 'info',
        '3': 'primary',
        '4': 'success',
        '5': 'danger'
      }
      return typeMap[status] || 'info'
    },
    getPriorityText(priority) {
      const priorityMap = {
        '1': '高',
        '2': '中',
        '3': '低'
      }
      return priorityMap[priority] || priority
    },
    getPriorityTagType(priority) {
      const typeMap = {
        '1': 'danger',
        '2': 'warning',
        '3': 'info'
      }
      return typeMap[priority] || 'info'
    },
    formatTime(time) {
      if (!time) return ''
      return new Date(time).toLocaleString('zh-CN')
    },
    goToDetail(taskId) {
      this.$router.push(`/task/${taskId}`)
    },
    goToSessionDetail(sessionId) {
      this.$router.push(`/session/${sessionId}`)
    },
    goBack() {
      this.$router.push('/tasks')
    },
    editTask() {
      this.$router.push(`/tasks/edit/${this.taskId}`)
    },
    async deleteTask() {
      try {
        await this.$confirm('确定要删除该任务吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await deleteTask(this.taskId)
        this.$message.success('任务删除成功')
        this.$router.push('/tasks')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除任务失败:', error)
          this.$message.error(`删除失败: ${error.message || '未知错误'}`)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.task-detail-page {
  padding: 20px;
  min-height: calc(100vh - 84px);
  background-color: var(--bg-page);
}

.page-header {
  margin-bottom: 20px;
}

.detail-content {
  margin: 0 !important;
}

/* 卡片通用样式 */
.info-card,
.task-progress,
.sub-tasks,
.task-dependencies,
.info-sidebar,
.task-history,
.session-association,
.action-card {
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.task-count,
.progress-count {
  font-size: 13px;
  color: var(--text-muted);
}

/* 任务基本信息 */
.basic-info {
  padding: 8px 0;

  .task-priority-badge {
    margin-bottom: 12px;
  }

  .task-title {
    margin: 0 0 16px 0;
    font-size: 20px;
    color: var(--text-primary);
    line-height: 1.4;
    font-weight: 600;
  }

  .desc {
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 16px;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    color: var(--text-muted);
    font-size: 13px;

    span {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    i {
      font-size: 14px;
    }
  }
}

/* 任务进度 */
.task-progress {
  margin-top: 20px;
}

.progress-content {
  padding: 8px 0;
}

.progress-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  .progress-label {
    font-size: 14px;
    color: var(--text-secondary);
  }

  .progress-value {
    font-size: 20px;
    font-weight: 600;
    color: var(--primary-color);
  }
}

/* 子任务 */
.sub-tasks {
  margin-top: 20px;
}

/* 任务依赖 */
.task-dependencies {
  margin-top: 20px;
}

/* 任务信息侧边栏 */
.info-sidebar {
  .info-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 6px;

  .label {
    font-size: 12px;
    color: var(--text-muted);
  }

  .value {
    font-size: 14px;
    color: var(--text-primary);
    word-break: break-all;

    &.code {
      font-family: monospace;
      font-size: 12px;
      color: var(--text-secondary);
      background-color: var(--bg-page);
      padding: 4px 8px;
      border-radius: var(--radius-sm);
    }
  }
}

/* 依赖、历史、会话列表 */
.dependency-list,
.history-list,
.session-list {
  margin-top: 12px;
}

.dependency-item,
.history-item,
.session-item {
  margin-bottom: 8px;
  padding: 12px;
  background-color: var(--bg-page);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-light);
  transition: all 0.3s;

  &:hover {
    background-color: var(--bg-card);
    box-shadow: var(--shadow-sm);
  }
}

.dep-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dep-name {
  flex: 1;
  margin-right: 8px;
  color: var(--text-primary);
  font-size: 14px;
}

.dep-status {
  flex-shrink: 0;
}

.history-time,
.session-time {
  color: var(--text-muted);
  font-size: 12px;
  margin-bottom: 4px;
}

.history-content {
  font-size: 14px;
  color: var(--text-secondary);
}

.session-name {
  flex: 1;
  margin-right: 8px;
  font-size: 14px;
  color: var(--text-primary);
  margin-bottom: 4px;
}

/* 任务历史 */
.task-history {
  margin-top: 20px;
}

/* 关联会话 */
.session-association {
  margin-top: 20px;
}

/* 操作按钮 */
.action-card {
  margin-top: 20px;
}

.action-buttons {
  display: flex;
  flex-direction: column;
}

/* 空状态 */
.empty-state {
  text-align: center;
  color: var(--text-disabled);
  padding: 40px 20px;

  p {
    margin-top: 12px;
    font-size: 14px;
  }
}
</style>
